/**
 * Bridge Club Network Overview
 *
 * Comprehensive analytics dashboard showing:
 * - Network-wide statistics
 * - Laura AI analysis and insights
 * - Top stores and products
 * - Growth trends
 * - Quick access to store/member details
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import api from '../../services/api';
import LauraInsightsPanel from '../../components/Admin/LauraInsightsPanel';
import '../../components/Admin/LauraInsightsPanel.css';
import './BridgeClubPages.css';
import './BridgeClubNetworkOverview.css';

interface Metrics {
  members: {
    total: number;
    active: number;
    active_rate: number;
    new_this_month: number;
    new_last_month: number;
    growth_rate: number;
    dormant: number;
    near_redemption: number;
  };
  stores: {
    total: number;
    active: number;
  };
  revenue: {
    total: number;
    this_month: number;
    last_month: number;
    growth_rate: number;
    avg_order: number;
  };
  engagement: {
    total_purchases: number;
    purchases_this_month: number;
    redemptions: number;
    avg_purchases_per_member: number;
  };
}

interface Store {
  id: number;
  name: string;
  city: string;
  state: string;
  member_count: number;
  purchase_count: number;
  revenue: number;
}

interface Product {
  name: string;
  quantity: number;
  revenue: number;
}

interface LauraAnalysis {
  executive_summary: string;
  health_score: number;
  key_insights: Array<{
    insight: string;
    type: 'success' | 'warning' | 'opportunity';
    metric: string;
    value: string;
  }>;
  recommendations: Array<{
    action: string;
    priority: 'high' | 'medium' | 'low';
    expected_impact: string;
  }>;
  trends: {
    member_growth: 'up' | 'down' | 'stable';
    revenue: 'up' | 'down' | 'stable';
    engagement: 'up' | 'down' | 'stable';
  };
  ai_generated: boolean;
}

interface NetworkData {
  metrics: Metrics;
  top_stores: Store[];
  top_products: Product[];
  trends: {
    member_growth: Array<{ month: string; count: number }>;
    revenue: Array<{ month: string; revenue: number; purchases: number }>;
  };
  laura_analysis: LauraAnalysis;
}

const COLORS = ['#8B0000', '#1a472a', '#B8860B', '#3B82F6', '#8884d8', '#82ca9d'];

const BridgeClubNetworkOverview: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<NetworkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<NetworkData>('/admin/bridge-club/network-overview/');
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load network overview:', err);
      setError(err.response?.data?.error || 'Failed to load network overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading network analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bridge-club-page">
        <div className="page-header">
          <div className="header-content">
            <button className="back-button" onClick={() => navigate('/admin/bridge-club')}>
              &larr; Back to Bridge Club
            </button>
            <h1>Network Overview</h1>
          </div>
        </div>
        <div className="error-banner">
          {error || 'Failed to load data'}
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const { metrics, top_stores, top_products, trends, laura_analysis } = data;

  // Prepare pie chart data for stores
  const storesPieData = top_stores.slice(0, 5).map(store => ({
    name: store.name,
    value: store.revenue
  }));

  return (
    <div className="bridge-club-page network-overview">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club')}>
            &larr; Back to Bridge Club
          </button>
          <h1>Network Overview</h1>
          <p>Comprehensive analytics for Bridge Club program</p>
        </div>
        <div className="header-actions">
          <button
            className="nav-btn"
            onClick={() => navigate('/admin/bridge-club/opportunities')}
          >
            View Opportunities
          </button>
          <button
            className="nav-btn secondary"
            onClick={() => navigate('/admin/bridge-club/qr-analytics')}
          >
            QR Analytics
          </button>
        </div>
      </div>

      {/* Laura AI Analysis Panel */}
      <div className="laura-section">
        <LauraInsightsPanel
          analysis={laura_analysis}
          loading={false}
          title="Laura's Network Analysis"
        />
      </div>

      {/* Key Metrics */}
      <div className="stats-row">
        <div className="stat-card highlight">
          <div className="stat-value">{metrics.members.total.toLocaleString()}</div>
          <div className="stat-label">Total Members</div>
          <div className="stat-trend">
            <span className={metrics.members.growth_rate >= 0 ? 'positive' : 'negative'}>
              {formatPercent(metrics.members.growth_rate)}
            </span>
            <span className="trend-label">vs last month</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{metrics.members.active.toLocaleString()}</div>
          <div className="stat-label">Active Members (90d)</div>
          <div className="stat-trend">
            <span className="neutral">{metrics.members.active_rate}%</span>
            <span className="trend-label">activity rate</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(metrics.revenue.this_month)}</div>
          <div className="stat-label">Revenue This Month</div>
          <div className="stat-trend">
            <span className={metrics.revenue.growth_rate >= 0 ? 'positive' : 'negative'}>
              {formatPercent(metrics.revenue.growth_rate)}
            </span>
            <span className="trend-label">vs last month</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{metrics.stores.active}</div>
          <div className="stat-label">Active Stores</div>
          <div className="stat-trend">
            <span className="neutral">of {metrics.stores.total}</span>
            <span className="trend-label">total stores</span>
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <span className="metric-icon dormant">!</span>
          <div className="metric-content">
            <div className="metric-value">{metrics.members.dormant}</div>
            <div className="metric-label">Dormant Members</div>
          </div>
        </div>
        <div className="metric-card">
          <span className="metric-icon near">*</span>
          <div className="metric-content">
            <div className="metric-value">{metrics.members.near_redemption}</div>
            <div className="metric-label">Near Redemption</div>
          </div>
        </div>
        <div className="metric-card">
          <span className="metric-icon">$</span>
          <div className="metric-content">
            <div className="metric-value">{formatCurrency(metrics.revenue.avg_order)}</div>
            <div className="metric-label">Avg Order Value</div>
          </div>
        </div>
        <div className="metric-card">
          <span className="metric-icon">R</span>
          <div className="metric-content">
            <div className="metric-value">{metrics.engagement.redemptions}</div>
            <div className="metric-label">Total Redemptions</div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        {/* Member Growth Chart */}
        <div className="chart-card">
          <h3>Member Growth (6 months)</h3>
          {trends.member_growth.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={trends.member_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1a472a" name="New Members" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Revenue Trend Chart */}
        <div className="chart-card">
          <h3>Revenue Trend (6 months)</h3>
          {trends.revenue.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trends.revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8B0000"
                  strokeWidth={2}
                  name="Revenue"
                  dot={{ fill: '#8B0000' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Stores and Products Row */}
      <div className="data-grid">
        {/* Top Stores */}
        <div className="data-card">
          <div className="card-header">
            <h3>Top Stores</h3>
            <button
              className="view-all-btn"
              onClick={() => navigate('/admin/bridge-club', { state: { tab: 'stores' } })}
            >
              View All
            </button>
          </div>
          <div className="stores-list">
            {top_stores.slice(0, 5).map((store, index) => (
              <div
                key={store.id}
                className="store-item"
                onClick={() => navigate(`/admin/bridge-club/stores/${store.id}`)}
              >
                <div className="store-rank">#{index + 1}</div>
                <div className="store-info">
                  <div className="store-name">{store.name}</div>
                  <div className="store-location">{store.city}, {store.state}</div>
                </div>
                <div className="store-stats">
                  <div className="store-revenue">{formatCurrency(store.revenue)}</div>
                  <div className="store-members">{store.member_count} members</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Store Revenue Distribution */}
        <div className="data-card">
          <h3>Revenue by Store</h3>
          {storesPieData.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={storesPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }: { name?: string; percent?: number }) => `${(name || '').substring(0, 15)}... (${((percent || 0) * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {storesPieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top Products */}
      <div className="chart-card full-width">
        <h3>Top Products</h3>
        <div className="products-table">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Product</th>
                <th>Quantity Sold</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {top_products.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-row">No product data available</td>
                </tr>
              ) : (
                top_products.slice(0, 10).map((product, index) => (
                  <tr key={product.name}>
                    <td className="rank">#{index + 1}</td>
                    <td>{product.name}</td>
                    <td className="center">{product.quantity.toLocaleString()}</td>
                    <td className="currency">{formatCurrency(product.revenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubNetworkOverview;
